import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import axios from "axios";
import { Helmet } from "react-helmet";
import { ArrowRight, X, Calendar, Info } from "lucide-react";
import { URLS } from "../Url";
import { mediaSrc } from "../utils/media";

const API_URL = URLS.GetGetAllCakes || "https://api.carnivalcastle.com/v1/carnivalApi/web/category/getcakes";
const BASE_URL = "https://api.carnivalcastle.com/";

// Curated vegetarian fallback cakes if API is unreachable or empty
const FALLBACK_PREMIUM_CAKES = [
  {
    _id: "fb_p1",
    name: "Royal Belgian Chocolate Truffle",
    image: "/img/c2.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
  {
    _id: "fb_p2",
    name: "Red Velvet Romance Celebration",
    image: "/img/c1.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
  {
    _id: "fb_p3",
    name: "Lotus Biscoff Caramel Elegance",
    image: "/img/c5.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
  {
    _id: "fb_p4",
    name: "Ferrero Rocher Hazelnut Luxe",
    image: "/img/c6.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
  {
    _id: "fb_p5",
    name: "Artisan Dark Chocolate Truffle",
    image: "/img/c7.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
  {
    _id: "fb_p6",
    name: "Vintage Buttercream Floral Cake",
    image: "/img/c8.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
];

const FALLBACK_STANDARD_CAKES = [
  {
    _id: "fb_s1",
    name: "Classic Dutch Chocolate Cake",
    image: "/img/c3.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
  {
    _id: "fb_s2",
    name: "Madagascar Vanilla Celebration",
    image: "/img/c4.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
  {
    _id: "fb_s3",
    name: "Black Forest Traditional",
    image: "/img/c1.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
  {
    _id: "fb_s4",
    name: "Fresh Strawberry Glaze Cake",
    image: "/img/c5.jpg",
    cakeType: "eggless",
    isFallback: true,
  },
];

function PureVegBadge() {
  return (
    <span className="ck-veg-pill">
      <span className="ck-veg-box">
        <span className="ck-veg-circle" />
      </span>
      <span className="ck-veg-label">PURE VEG</span>
    </span>
  );
}

export default function Cakes() {
  const navigate = useNavigate();
  const [premiumCakes, setPremiumCakes] = useState([]);
  const [normalCakes, setNormalCakes] = useState([]);
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'premium' | 'normal'
  const [loading, setLoading] = useState(true);
  const [selectedCake, setSelectedCake] = useState(null);

  useEffect(() => {
    let isMounted = true;
    axios
      .post(API_URL)
      .then((res) => {
        if (isMounted && res.data && res.data.success) {
          setPremiumCakes(res.data.premiumCakes || []);
          setNormalCakes(res.data.normalCakes || []);
        }
      })
      .catch((err) => {
        console.warn("Could not load cakes from API, utilizing authentic collection fallback.", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Strict Pure Vegetarian Filter - filters out any non-vegetarian cake
  const isVegetarian = useCallback((cake) => {
    if (!cake) return false;
    if (cake.cakeType && cake.cakeType.toLowerCase() === "egg") return false;
    return true;
  }, []);

  // Filtered Vegetarian Lists with Fallback support
  const vegPremium = useMemo(() => {
    const list = premiumCakes.filter(isVegetarian);
    return list.length > 0 ? list : FALLBACK_PREMIUM_CAKES;
  }, [premiumCakes, isVegetarian]);

  const vegStandard = useMemo(() => {
    const list = normalCakes.filter(isVegetarian);
    return list.length > 0 ? list : FALLBACK_STANDARD_CAKES;
  }, [normalCakes, isVegetarian]);

  const displayedCount = useMemo(() => {
    if (activeTab === "premium") return vegPremium.length;
    if (activeTab === "normal") return vegStandard.length;
    return vegPremium.length + vegStandard.length;
  }, [activeTab, vegPremium, vegStandard]);

  const getImageSrc = (cake) => {
    if (!cake || !cake.image) return "/img/c2.jpg";
    if (cake.isFallback || cake.image.startsWith("/")) {
      return cake.image;
    }
    return mediaSrc(cake.image);
  };

  return (
    <>
      <Helmet>
        <title>Celebration Cakes | Carnival Castle</title>
        <meta
          name="description"
          content="Artisanal 100% pure vegetarian celebration cakes for private theatre celebrations at Carnival Castle."
        />
      </Helmet>

      <div className="ck-page-root">
        <Header />

        {/* ── Compact Editorial Hero (Solid Colors, Zero Gradients) ── */}
        <section className="ck-hero-section">
          <div className="ck-hero-container">
            <span className="ck-hero-eyebrow">✦ CURATED FOR YOUR CELEBRATION ✦</span>
            <h1 className="ck-hero-title">Cakes Made for Moments</h1>
            <p className="ck-hero-desc">
              Beautifully curated vegetarian cakes to complete your celebration at Carnival Castle.
            </p>

            <div className="ck-hero-features">
              <span className="ck-feature-item">
                <span className="ck-veg-box mini">
                  <span className="ck-veg-circle mini" />
                </span>
                100% Pure Vegetarian
              </span>
              <span className="ck-feature-dot">·</span>
              <span className="ck-feature-item">Freshly Prepared</span>
              <span className="ck-feature-dot">·</span>
              <span className="ck-feature-item">Private Theatre Dining</span>
            </div>
          </div>
        </section>

        {/* ── Sticky Collection Filter Navigation ── */}
        <div className="ck-nav-wrapper">
          <div className="ck-container">
            <div className="ck-nav-row">
              <div className="ck-filter-selector">
                <button
                  type="button"
                  className={`ck-filter-btn ${activeTab === "all" ? "active" : ""}`}
                  onClick={() => setActiveTab("all")}
                >
                  All Collections
                </button>
                <button
                  type="button"
                  className={`ck-filter-btn ${activeTab === "premium" ? "active" : ""}`}
                  onClick={() => setActiveTab("premium")}
                >
                  ✦ Premium Cakes
                </button>
                <button
                  type="button"
                  className={`ck-filter-btn ${activeTab === "normal" ? "active" : ""}`}
                  onClick={() => setActiveTab("normal")}
                >
                  Standard Cakes
                </button>
              </div>

              <div className="ck-nav-info">
                <PureVegBadge />
                <span className="ck-nav-count-text">
                  <strong>{displayedCount}</strong> Pure Veg Cakes
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Cakes Collection Content ── */}
        <main className="ck-main-content">
          <div className="ck-container">
            {loading ? (
              <div className="ck-loading-state">
                <div className="ck-spinner" />
                <p>Curating celebration cakes...</p>
              </div>
            ) : (
              <>
                {/* ── 1. PREMIUM CAKES COLLECTION ── */}
                {(activeTab === "all" || activeTab === "premium") && (
                  <section className="ck-collection-section" id="premium-cakes">
                    <div className="ck-section-header">
                      <div className="ck-section-title-wrap">
                        <span className="ck-section-eyebrow">✦ SIGNATURE CREATIONS ✦</span>
                        <h2 className="ck-section-title">The Premium Collection</h2>
                        <p className="ck-section-desc">
                          Artisanal celebration cakes with bespoke artistry, designed for milestone occasions.
                        </p>
                      </div>

                      <div className="ck-advance-notice">
                        <Calendar size={14} className="ck-notice-icon" />
                        <span>Order at least 3 days in advance for bespoke designs</span>
                      </div>
                    </div>

                    <div className="ck-premium-grid">
                      {vegPremium.map((cake, idx) => (
                        <article
                          key={cake._id || idx}
                          className="ck-card ck-card-premium"
                          onClick={() => setSelectedCake({ ...cake, collection: "Premium" })}
                        >
                          <div className="ck-img-container ck-img-container-premium">
                            <img
                              src={getImageSrc(cake)}
                              alt={cake.name}
                              className="ck-image"
                              loading="lazy"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/img/c2.jpg";
                              }}
                            />
                            <span className="ck-card-tag">PREMIUM</span>
                          </div>

                          <div className="ck-card-body ck-card-body-premium">
                            <div className="ck-card-header">
                              <PureVegBadge />
                            </div>

                            <h3 className="ck-card-title">{cake.name}</h3>

                            <div className="ck-card-footer">
                              <span className="ck-card-action">
                                View Cake <ArrowRight size={14} className="ck-arrow" />
                              </span>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                )}

                {/* ── 2. STANDARD CAKES COLLECTION ── */}
                {(activeTab === "all" || activeTab === "normal") && (
                  <section
                    className={`ck-collection-section ${activeTab === "all" ? "ck-section-divider" : ""}`}
                    id="standard-cakes"
                  >
                    <div className="ck-section-header">
                      <div className="ck-section-title-wrap">
                        <span className="ck-section-eyebrow">✦ TIMELESS DELIGHTS ✦</span>
                        <h2 className="ck-section-title">The Standard Collection</h2>
                        <p className="ck-section-desc">
                          Beloved celebration flavors prepared fresh with 100% pure vegetarian ingredients.
                        </p>
                      </div>
                    </div>

                    <div className="ck-standard-grid">
                      {vegStandard.map((cake, idx) => (
                        <article
                          key={cake._id || idx}
                          className="ck-card ck-card-standard"
                          onClick={() => setSelectedCake({ ...cake, collection: "Standard" })}
                        >
                          <div className="ck-img-container ck-img-container-standard">
                            <img
                              src={getImageSrc(cake)}
                              alt={cake.name}
                              className="ck-image"
                              loading="lazy"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/img/c1.jpg";
                              }}
                            />
                          </div>

                          <div className="ck-card-body ck-card-body-standard">
                            <div className="ck-card-header">
                              <PureVegBadge />
                            </div>

                            <h3 className="ck-card-title standard">{cake.name}</h3>

                            <div className="ck-card-footer">
                              <span className="ck-card-action">
                                View Cake <ArrowRight size={13} className="ck-arrow" />
                              </span>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                )}

                {/* ── Refined Celebration Concierge CTA ── */}
                <section className="ck-bottom-banner">
                  <div className="ck-bottom-content">
                    <span className="ck-bottom-eyebrow">✦ PRIVATE THEATRE CELEBRATIONS ✦</span>
                    <h2 className="ck-bottom-title">Ready to Plan Your Perfect Celebration?</h2>
                    <p className="ck-bottom-desc">
                      Pair your theatre screening with our signature vegetarian cakes, personalized decoration, and gourmet refreshments.
                    </p>
                    <div className="ck-bottom-actions">
                      <Link to="/enquiry" className="ck-btn-primary">
                        Plan Your Celebration <ArrowRight size={15} />
                      </Link>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        </main>

        {/* ── Cake Detail Modal ── */}
        {selectedCake && (
          <div className="ck-modal-backdrop" onClick={() => setSelectedCake(null)}>
            <div
              className="ck-modal-card"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="ck-modal-close"
                onClick={() => setSelectedCake(null)}
                aria-label="Close cake modal"
              >
                <X size={18} />
              </button>

              <div className="ck-modal-media">
                <img
                  src={getImageSrc(selectedCake)}
                  alt={selectedCake.name}
                  className="ck-modal-img"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/img/c2.jpg";
                  }}
                />
              </div>

              <div className="ck-modal-content">
                <div className="ck-modal-header">
                  <PureVegBadge />
                  <span className="ck-modal-badge">{selectedCake.collection} Collection</span>
                </div>

                <h3 className="ck-modal-title">{selectedCake.name}</h3>

                <p className="ck-modal-description">
                  Crafted specially for your private celebration at Carnival Castle. Handcrafted with fine vegetarian ingredients for an exquisite taste experience.
                </p>

                {selectedCake.collection === "Premium" && (
                  <div className="ck-modal-notice">
                    <Info size={14} className="ck-notice-icon" />
                    <span>Customized premium cakes must be requested at least 3 days in advance.</span>
                  </div>
                )}

                <div className="ck-modal-actions">
                  <button
                    type="button"
                    className="ck-btn-primary full-width"
                    onClick={() => {
                      setSelectedCake(null);
                      navigate("/enquiry");
                    }}
                  >
                    Select for Celebration <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>

      <style>{`
        /* ── Typography & Global Tokens (Solid Palette, Zero Gradients) ── */
        .ck-page-root {
          background-color: #FAF6F0;
          background: radial-gradient(120% 70% at 50% 0%, rgba(94, 14, 115, 0.03) 0%, transparent 65%),
                      radial-gradient(90% 60% at 100% 100%, rgba(201, 168, 76, 0.04) 0%, transparent 60%),
                      #FAF6F0;
          color: #2D2833;
          min-height: 100vh;
        }

        .ck-container {
          max-width: 1360px;
          width: calc(100% - 48px);
          margin: 0 auto;
          padding: 0;
        }

        /* ── Hero Section ── */
        .ck-hero-section {
          background-color: #1A0030;
          color: #FFFFFF;
          padding: 40px 20px 32px;
          text-align: center;
          border-bottom: 1px solid rgba(201, 168, 76, 0.18);
        }

        .ck-hero-container {
          max-width: 760px;
          margin: 0 auto;
        }

        .ck-hero-eyebrow {
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2.2px;
          color: #C9A84C;
          text-transform: uppercase;
          margin-bottom: 12px;
        }

        .ck-hero-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(32px, 4.5vw, 48px);
          font-weight: 700;
          color: #FFFFFF;
          line-height: 1.15;
          margin: 0 0 12px;
        }

        .ck-hero-desc {
          font-size: clamp(14px, 1.8vw, 16px);
          color: rgba(255, 255, 255, 0.78);
          line-height: 1.6;
          max-width: 600px;
          margin: 0 auto 20px;
        }

        .ck-hero-features {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 12px;
          font-size: 12.5px;
          color: #E8D5A0;
          font-weight: 500;
        }

        .ck-feature-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .ck-feature-dot {
          color: rgba(201, 168, 76, 0.4);
        }

        /* ── Pure Vegetarian Badge ── */
        .ck-veg-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background-color: #F1F8F1;
          border: 1px solid #C5E1A5;
          border-radius: 4px;
          padding: 2px 6px;
        }

        .ck-veg-box {
          width: 11px;
          height: 11px;
          border: 1.2px solid #2E7D32;
          border-radius: 2px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #FFFFFF;
        }

        .ck-veg-box.mini {
          width: 10px;
          height: 10px;
        }

        .ck-veg-circle {
          width: 4px;
          height: 4px;
          background-color: #2E7D32;
          border-radius: 50%;
        }

        .ck-veg-circle.mini {
          width: 3.5px;
          height: 3.5px;
        }

        .ck-veg-label {
          font-size: 8.5px;
          font-weight: 700;
          letter-spacing: 0.6px;
          color: #2E7D32;
          text-transform: uppercase;
        }

        /* ── Sticky Collection Filter Bar ── */
        .ck-nav-wrapper {
          background-color: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          border-bottom: 1px solid #EBE2D5;
          position: sticky;
          top: 0;
          z-index: 40;
          padding: 12px 0;
          box-shadow: 0 4px 16px rgba(26, 0, 48, 0.03);
        }

        .ck-nav-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }

        .ck-filter-selector {
          display: inline-flex;
          background-color: #F8F5F0;
          border: 1px solid #E4DCCF;
          border-radius: 8px;
          padding: 4px;
          gap: 4px;
        }

        .ck-filter-btn {
          border: none;
          background: transparent;
          color: #5A5260;
          font-size: 13px;
          font-weight: 600;
          padding: 8px 18px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .ck-filter-btn:hover {
          color: #1A0030;
        }

        .ck-filter-btn.active {
          background-color: #1E0038;
          color: #C9A84C;
          border: 1px solid rgba(201, 168, 76, 0.3);
          box-shadow: 0 2px 8px rgba(30, 0, 56, 0.12);
        }

        .ck-nav-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .ck-nav-count-text {
          font-size: 12.5px;
          color: #7A7082;
        }

        .ck-nav-count-text strong {
          color: #1A0030;
          font-weight: 700;
        }

        /* ── Main Content Area ── */
        .ck-main-content {
          padding: 22px 0 40px;
        }

        .ck-collection-section {
          margin-bottom: 28px;
        }

        .ck-section-divider {
          padding-top: 24px;
          border-top: 1px solid #EBE4D8;
        }

        .ck-section-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 14px;
        }

        .ck-section-eyebrow {
          display: block;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 2px;
          color: #B89535;
          text-transform: uppercase;
          margin-bottom: 6px;
        }

        .ck-section-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(24px, 3.2vw, 32px);
          font-weight: 700;
          color: #1A0030;
          margin: 0 0 6px;
        }

        .ck-section-desc {
          font-size: 13.5px;
          color: #6D6375;
          margin: 0;
          max-width: 580px;
        }

        .ck-advance-notice {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background-color: #FAF4E6;
          border: 1px solid #E8D5A0;
          border-radius: 6px;
          padding: 8px 14px;
          font-size: 12px;
          font-weight: 600;
          color: #8C6D1F;
        }

        .ck-notice-icon {
          color: #B89535;
          flex-shrink: 0;
        }

        /* ── Grids (Unified 6-Column Layout - Clean & Premium) ── */
        .ck-premium-grid,
        .ck-standard-grid {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 13px;
        }

        /* ── Cake Card Architecture (Clean & Premium Boutique) ── */
        .ck-card {
          background-color: #FFFFFF;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          border: 1px solid #ECE5DA;
          box-shadow: 0 3px 12px rgba(26, 0, 48, 0.035);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }

        .ck-card-premium {
          border: 1px solid #E8DFD0;
        }

        .ck-card-premium:hover {
          transform: translateY(-4px);
          border-color: #C9A84C;
          box-shadow: 0 12px 26px rgba(94, 14, 115, 0.09);
        }

        .ck-card-standard {
          border: 1px solid #ECE5DC;
        }

        .ck-card-standard:hover {
          transform: translateY(-4px);
          border-color: #B89535;
          box-shadow: 0 10px 22px rgba(94, 14, 115, 0.07);
        }

        /* Image Display - Spacious, Clean & Pure */
        .ck-img-container {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          background-color: #FAF8F5;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 1px solid #F0ECE4;
        }

        .ck-img-container-premium,
        .ck-img-container-standard {
          aspect-ratio: 1 / 1;
          height: auto;
        }

        .ck-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          display: block;
          padding: 6px;
          box-sizing: border-box;
          transition: transform 0.35s ease;
        }

        .ck-card:hover .ck-image {
          transform: scale(1.04);
        }

        .ck-card-tag {
          position: absolute;
          top: 7px;
          right: 7px;
          background-color: #1E0038;
          color: #C9A84C;
          font-size: 7.5px;
          font-weight: 700;
          letter-spacing: 0.8px;
          padding: 2.5px 7px;
          border-radius: 4px;
          border: 1px solid rgba(201, 168, 76, 0.35);
          z-index: 2;
        }

        /* Card Body - Clean, Balanced Typography */
        .ck-card-body {
          padding: 9px 11px 11px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .ck-card-body-premium,
        .ck-card-body-standard {
          padding: 9px 11px 11px;
        }

        .ck-card-header {
          margin-bottom: 4px;
        }

        .ck-card-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 12.5px;
          font-weight: 700;
          color: #1A0030;
          line-height: 1.3;
          margin: 0 0 6px;
          transition: color 0.2s ease;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 33px;
        }

        .ck-card-title.standard {
          font-size: 12px;
        }

        .ck-card:hover .ck-card-title {
          color: #5E0E73;
        }

        .ck-card-footer {
          margin-top: auto;
          padding-top: 6px;
          border-top: 1px solid #F3EDE5;
        }

        .ck-card-action {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          font-weight: 600;
          color: #5E0E73;
          letter-spacing: 0.2px;
        }

        .ck-arrow {
          transition: transform 0.2s ease;
        }

        .ck-card:hover .ck-arrow {
          transform: translateX(3px);
        }

        /* ── Bottom Luxury CTA Banner ── */
        .ck-bottom-banner {
          background-color: #1E0038;
          border-radius: 18px;
          padding: 48px 36px;
          text-align: center;
          color: #FFFFFF;
          margin-top: 56px;
          border: 1px solid rgba(201, 168, 76, 0.2);
        }

        .ck-bottom-content {
          max-width: 680px;
          margin: 0 auto;
        }

        .ck-bottom-eyebrow {
          display: inline-block;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 2px;
          color: #C9A84C;
          text-transform: uppercase;
          margin-bottom: 10px;
        }

        .ck-bottom-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(26px, 3.5vw, 36px);
          font-weight: 700;
          color: #FFFFFF;
          margin: 0 0 12px;
          line-height: 1.2;
        }

        .ck-bottom-desc {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.76);
          line-height: 1.6;
          margin: 0 auto 28px;
        }

        .ck-bottom-actions {
          display: flex;
          justify-content: center;
          gap: 14px;
          flex-wrap: wrap;
        }

        .ck-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background-color: #5E0E73;
          color: #FFFFFF;
          border: 1px solid #C9A84C;
          border-radius: 8px;
          padding: 12px 26px;
          font-size: 13.5px;
          font-weight: 600;
          letter-spacing: 0.4px;
          text-decoration: none;
          cursor: pointer;
          transition: background-color 0.2s ease, transform 0.2s ease;
        }

        .ck-btn-primary:hover {
          background-color: #4A0B5B;
          color: #FFFFFF;
          transform: translateY(-1px);
        }

        .ck-btn-primary.full-width {
          width: 100%;
          height: 46px;
        }

        .ck-btn-outline {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background-color: transparent;
          color: #FFFFFF;
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 8px;
          padding: 12px 24px;
          font-size: 13.5px;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          transition: background-color 0.2s ease, border-color 0.2s ease;
        }

        .ck-btn-outline:hover {
          background-color: rgba(255, 255, 255, 0.08);
          border-color: #FFFFFF;
          color: #FFFFFF;
        }

        /* ── Modal Experience ── */
        .ck-modal-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(18, 0, 32, 0.7);
          backdrop-filter: blur(4px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .ck-modal-card {
          background-color: #FFFFFF;
          border-radius: 16px;
          max-width: 500px;
          width: 100%;
          overflow: hidden;
          position: relative;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
          border: 1px solid #EBE4D8;
        }

        .ck-modal-close {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: rgba(26, 0, 48, 0.7);
          color: #FFFFFF;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 2;
          transition: background-color 0.2s ease;
        }

        .ck-modal-close:hover {
          background-color: #1A0030;
        }

        .ck-modal-media {
          width: 100%;
          height: 300px;
          background-color: #FAFAF7;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
          box-sizing: border-box;
        }

        .ck-modal-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          display: block;
        }

        .ck-modal-content {
          padding: 24px;
        }

        .ck-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .ck-modal-badge {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: #5E0E73;
        }

        .ck-modal-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 22px;
          font-weight: 700;
          color: #1A0030;
          margin: 0 0 10px;
        }

        .ck-modal-description {
          font-size: 13.5px;
          color: #6D6375;
          line-height: 1.55;
          margin: 0 0 16px;
        }

        .ck-modal-notice {
          display: flex;
          align-items: center;
          gap: 8px;
          background-color: #FAF4E6;
          border: 1px solid #E8D5A0;
          border-radius: 6px;
          padding: 10px 12px;
          font-size: 12px;
          color: #8C6D1F;
          margin-bottom: 20px;
        }

        .ck-modal-actions {
          margin-top: 8px;
        }

        /* ── Loading Spinner ── */
        .ck-loading-state {
          padding: 80px 20px;
          text-align: center;
          color: #7A7082;
        }

        .ck-spinner {
          width: 36px;
          height: 36px;
          border: 3px solid #EBE4D8;
          border-top-color: #5E0E73;
          border-radius: 50%;
          margin: 0 auto 14px;
          animation: ck-spin 0.8s linear infinite;
        }

        @keyframes ck-spin {
          to { transform: rotate(360deg); }
        }

        /* ── Responsive Adaptations ── */
        @media (max-width: 1280px) {
          .ck-premium-grid,
          .ck-standard-grid {
            grid-template-columns: repeat(5, 1fr);
            gap: 12px;
          }
        }

        @media (max-width: 1040px) {
          .ck-premium-grid,
          .ck-standard-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 11px;
          }
        }

        @media (max-width: 780px) {
          .ck-container {
            width: calc(100% - 32px);
          }
          .ck-hero-section {
            padding: 30px 14px 22px;
          }
          .ck-nav-row {
            justify-content: center;
          }
          .ck-filter-selector {
            width: 100%;
            justify-content: center;
          }
          .ck-filter-btn {
            padding: 6px 10px;
            font-size: 11.5px;
          }
          .ck-premium-grid,
          .ck-standard-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
          }
          .ck-card-body {
            padding: 8px 9px 10px;
          }
          .ck-card-title {
            font-size: 12px;
            min-height: 30px;
          }
          .ck-bottom-banner {
            padding: 28px 16px;
            margin-top: 32px;
          }
        }

        @media (max-width: 480px) {
          .ck-container {
            width: calc(100% - 20px);
          }
          .ck-premium-grid,
          .ck-standard-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 9px;
          }
        }
      `}</style>
    </>
  );
}